import { useState, useEffect } from 'react';

import styles from './ContentEditor.module.css';
import EmbeddedCategoryManager from './EmbeddedCategoryManager';

/**
 * ShopGalleryEditor Component
 *
 * Editor for shop-gallery page type
 * Manages description and shop-specific display settings
 *
 * Note: Actual shop items are managed through the Image Manager
 */

/**
 *
 */
type ShopGalleryContent = {
  readonly description?: string;
  readonly settings?: {
    readonly enableCategories?: boolean;
    readonly showPrices?: boolean;
  };
};

/**
 *
 */
type ShopGalleryEditorProps = {
  readonly slug: string;
  readonly pageTitle: string;
  readonly initialContent: Record<string, unknown>;
  readonly onSave: (content: Record<string, unknown>) => Promise<void>;
  readonly isSaving: boolean;
};

const ShopGalleryEditor = ({
  slug: _slug,
  pageTitle,
  initialContent,
  onSave,
  isSaving,
}: ShopGalleryEditorProps): JSX.Element => {
  const [description, setDescription] = useState('');
  const [enableCategories, setEnableCategories] = useState(true);
  const [showPrices, setShowPrices] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form from content
  useEffect(() => {
    const content = initialContent as ShopGalleryContent;
    setDescription(content.description ?? '');
    setEnableCategories(content.settings?.enableCategories ?? true);
    setShowPrices(content.settings?.showPrices ?? true);
  }, [initialContent]);

  // Track changes
  useEffect(() => {
    const content = initialContent as ShopGalleryContent;
    const hasChanged =
      description !== (content.description ?? '') ||
      enableCategories !== (content.settings?.enableCategories ?? true) ||
      showPrices !== (content.settings?.showPrices ?? true);
    setHasChanges(hasChanged);
  }, [description, enableCategories, showPrices, initialContent]);

  const handleSave = async () => {
    const content: ShopGalleryContent = {
      description,
      settings: {
        enableCategories,
        showPrices,
      },
    };

    try {
      await onSave(content);
      setHasChanges(false);
    } catch (err) {
      // Error handled by parent
    }
  };

  return (
    <div className={styles.editorContainer}>
      <div className={styles.editorHeader}>
        <div>
          <h1 className={styles.editorTitle}>{pageTitle}</h1>
          <p className={styles.editorSubtitle}>Shop Gallery Settings</p>
        </div>
        <button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className={styles.saveButton}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className={styles.editorContent}>
        {/* Description Section */}
        <div className={styles.section}>
          <label className={styles.label} htmlFor="description">
            Shop Description
          </label>
          <p className={styles.helpText}>
            Optional description shown at the top of the shop gallery
          </p>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={styles.textarea}
            rows={4}
            placeholder="Enter shop description..."
          />
        </div>

        {/* Settings Section */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Display Settings</h2>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={enableCategories}
                onChange={(e) => setEnableCategories(e.target.checked)}
              />
              <span>Enable Category Filtering</span>
            </label>
            <p className={styles.helpText}>
              Allow users to filter shop items by category (categories managed in Category Manager)
            </p>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={showPrices}
                onChange={(e) => setShowPrices(e.target.checked)}
              />
              <span>Show Prices</span>
            </label>
            <p className={styles.helpText}>
              Display price information on shop items in the gallery
            </p>
          </div>
        </div>

        {/* Info Section */}
        <div className={styles.infoBox}>
          <div className={styles.infoIcon}>ℹ️</div>
          <div>
            <p className={styles.infoTitle}>Managing Shop Items</p>
            <p className={styles.infoText}>
              Shop items are uploaded and managed through the{' '}
              <a href="/admin/images" className={styles.link}>
                Image Manager
              </a>
              . Assign images to categories with type "shop" and add price information to display
              them in this shop gallery.
            </p>
          </div>
        </div>

        {/* Category Manager Section */}
        {enableCategories ? <EmbeddedCategoryManager type="shop" /> : null}
      </div>
    </div>
  );
};

export default ShopGalleryEditor;
